import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { guardarDestino } from '@/lib/destino';
import Dica from '@/components/feature/Dica';
import RodapePassos from './components/RodapePassos';
import BlocoBasico, { type BlocoBasicoHandle } from './components/BlocoBasico';
import PassoParticipacoes, { type PassoParticipacoesHandle } from './components/PassoParticipacoes';
import PassoPagamento, { type PassoPagamentoHandle } from './components/PassoPagamento';
import PassoRifa, { type PassoRifaHandle } from './components/PassoRifa';
import PassoDatas, { type PassoDatasHandle } from './components/PassoDatas';
import PassoFicha, { type PassoFichaHandle } from './components/PassoFicha';
import PassoPublicar, { type PassoPublicarHandle } from './components/PassoPublicar';
import {
  combinarDataHora,
  draftDeEvento,
  eventoDraftVazio,
  mensagemErro,
  novaParticipacao,
  sessaoPerdida,
  slugificar,
  sugestoesParaCategoria,
} from './utils';
import type {
  CategoriaEvento,
  EstadoSalvamento,
  EventoDraft,
  FormaParticipar,
  ImagemDraft,
  MembroEquipe,
  ModoPagamento,
  PassoChave,
} from './types';
import { buscarPerfilCategoria, type PerfilCategoria } from '@/lib/perfilEvento';
import { carregarParticipacoes } from './carregarParticipacoes';

// Onde a pessoa parou: por porta + evento, só nesta aba (sessionStorage).
function chaveDoPasso(porta: string, eventoId: number): string {
  return `seliga:passo:${porta}:${eventoId}`;
}

const ROTULOS_PASSOS: Record<PassoChave, string> = {
  evento: 'Evento',
  inscricoes: 'Inscrições',
  pagamento: 'Como se paga',
  rifa: 'A ação entre amigos deste evento',
  datas: 'Datas do evento',
  ficha: 'Ficha',
  publicar: 'Publicar',
};

// Os passos seguem `passos_sugeridos` do banco (perfil_da_categoria). Sem padrão,
// cai na montagem antiga.
function montarPassos(
  modoPagamento: ModoPagamento,
  ehIngresso: boolean,
  padrao: PerfilCategoria | null,
): PassoChave[] {
  const sugeridos = padrao?.passos_sugeridos ?? null;
  if (sugeridos && sugeridos.length > 0) {
    const passos: PassoChave[] = [];
    if (sugeridos.includes('o_que') || sugeridos.includes('quando_onde')) passos.push('evento');
    if (sugeridos.includes('participacoes')) passos.push('inscricoes');
    if (sugeridos.includes('dinheiro')) passos.push('pagamento');
    if (modoPagamento === 'rifa' || sugeridos.includes('rifa')) passos.push('rifa');
    if (sugeridos.includes('ficha')) passos.push('ficha');
    if (sugeridos.includes('encontros')) passos.push('datas');
    passos.push('publicar');
    return passos;
  }
  const passos: PassoChave[] = ['evento', 'inscricoes', 'pagamento'];
  if (modoPagamento === 'rifa') passos.push('rifa');
  passos.push('datas');
  if (!ehIngresso) passos.push('ficha');
  passos.push('publicar');
  return passos;
}

type Estado = 'carregando' | 'negado' | 'nao-encontrado' | 'erro' | 'ok';

interface ModalidadeDB {
  id: number;
  nome: string;
  descricao: string | null;
  publica: boolean;
  inscricoes_abrem_em: string | null;
  inscricoes_fecham_em: string | null;
  vagas_total: number | null;
  valor_centavos: number;
  valor_definido: boolean;
  ordem: number;
  exige_identificacao: boolean;
  max_por_compra: number;
}


export default function AssistenteEventoPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const modoEdicao = !!id;
  const porta = searchParams.get('porta') || 'direto';
  const { user, pronto } = useAuth();

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);

  const [dono, setDono] = useState(false);
  const [eventoId, setEventoId] = useState<number | null>(null);
  const [passo, setPasso] = useState<PassoChave>('evento');
  const [padrao, setPadrao] = useState<PerfilCategoria | null>(null);
  const [formaExplicitada, setFormaExplicitada] = useState(false);

  const [draft, setDraft] = useState<EventoDraft>(eventoDraftVazio());
  const [participacoes, setParticipacoes] = useState<FormaParticipar[]>([]);
  const [equipe, setEquipe] = useState<MembroEquipe[]>([]);
  const [inscritosTotal, setInscritosTotal] = useState(0);
  const [faltamPublicar, setFaltamPublicar] = useState(1);

  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const timerSalvo = useRef<number | null>(null);

  const [perfilCidade, setPerfilCidade] = useState('');
  const [perfilUf, setPerfilUf] = useState('');
  const [estadoSalvamento, setEstadoSalvamento] = useState<EstadoSalvamento>('ocioso');

  const draftRef = useRef<EventoDraft>(draft);
  const eventoIdRef = useRef<number | null>(eventoId);
  const atualizadoEmRef = useRef<string | null>(null);
  const salvandoBasicoRef = useRef(false);
  const dirtyRef = useRef(false);
  const debounceRef = useRef<number | null>(null);
  const timerSalvoBasicoRef = useRef<number | null>(null);

  const refPasso1 = useRef<BlocoBasicoHandle>(null);
  const refPasso2 = useRef<PassoParticipacoesHandle>(null);
  const refPassoPagamento = useRef<PassoPagamentoHandle>(null);
  const refPassoRifa = useRef<PassoRifaHandle>(null);
  const refPasso3 = useRef<PassoDatasHandle>(null);
  const refPasso4 = useRef<PassoFichaHandle>(null);
  const refPasso5 = useRef<PassoPublicarHandle>(null);

  // Refs espelhadas para o salvamento automático (evita closure velha).
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    eventoIdRef.current = eventoId;
  }, [eventoId]);

  // A porta liga as chaves: buscamos o padrão da categoria no banco.
  useEffect(() => {
    if (modoEdicao) return;
    const cat = draft.categoria;
    if (!cat) {
      setPadrao(null);
      return;
    }
    let ativoCat = true;
    buscarPerfilCategoria(cat)
      .then((p) => {
        if (ativoCat) setPadrao(p);
      })
      .catch(() => {
        if (ativoCat) setPadrao(null);
      });
    return () => {
      ativoCat = false;
    };
  }, [draft.categoria, modoEdicao]);

  // Pré-marca "Compram ingresso ou preenchem ficha?" pelo padrão da categoria.
  useEffect(() => {
    if (modoEdicao || !padrao || formaExplicitada) return;
    setDraft((d) => ({
      ...d,
      formaParticipar: padrao.vende_ingressos ? 'ingresso' : 'inscricao',
    }));
  }, [padrao, formaExplicitada, modoEdicao]);

  const passos = useMemo(
    () => montarPassos(draft.modoPagamento, draft.formaParticipar === 'ingresso', padrao),
    [draft.modoPagamento, draft.formaParticipar, padrao],
  );

  // Se o passo salvo não existe nesta sequência, começa do primeiro.
  useEffect(() => {
    if (passos.length === 0) return;
    if (passos.includes(passo)) return;
    setPasso(passos[0]);
  }, [passos, passo]);

  // Lembra onde a pessoa parou, por evento, só nesta aba.
  useEffect(() => {
    if (!eventoId) return;
    if (!passos.includes(passo)) return;
    sessionStorage.setItem(chaveDoPasso(porta, eventoId), passo);
  }, [eventoId, passo, passos, porta]);

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      const destino = modoEdicao ? `/evento/${id}/editar` : '/evento/novo';
      guardarDestino(destino);
      navigate(`/entrar?voltar=${encodeURIComponent(destino)}`, { replace: true });
      return;
    }
    let ativo = true;

    async function carregar() {
      setEstado('carregando');
      setErro(null);
      try {
        if (!modoEdicao) {
          const catParam = searchParams.get('categoria');
          const formaParam = searchParams.get('forma');

          const categoriasValidas: CategoriaEvento[] = [
            'acampamento',
            'retiro',
            'curso',
            'encontro',
            'congresso',
            'show',
            'formacao',
            'missa',
            'confraternizacao',
            'quermesse',
            'outro',
          ];
          const cat = catParam && categoriasValidas.includes(catParam as CategoriaEvento)
            ? (catParam as CategoriaEvento)
            : 'outro';
          const forma = formaParam === 'ingresso' ? 'ingresso' : 'inscricao';

          // Retoma um rascunho já criado nesta porta (evita duplicar).
          const chave = `rascunho:${porta}`;
          const draftId = localStorage.getItem(chave);
          if (draftId) {
            const idNum = Number(draftId);
            if (Number.isFinite(idNum) && idNum > 0) {
              const { data: evRes } = await supabase
                .from('eventos')
                .select('*')
                .eq('id', idNum)
                .eq('dono_id', user.id)
                .eq('status', 'rascunho')
                .maybeSingle();
              if (evRes) {
                const evResData = evRes as Record<string, unknown>;
                // Traz do banco o que já foi salvo: tipos, grupos, valores e fichas.
                // Sem isso, o Passo 2 achava que estava vazio e pedia para criar de novo.
                let carregado: Awaited<ReturnType<typeof carregarParticipacoes>> | null = null;
                try {
                  carregado = await carregarParticipacoes(idNum);
                } catch {
                  carregado = null;
                }
                if (ativo) {
                  setDono(true);
                  setEventoId(idNum);
                  atualizadoEmRef.current = (evResData.atualizado_em as string) || null;
                  setDraft(draftDeEvento(evResData));
                  if (carregado) {
                    setParticipacoes(carregado.lista);
                    setInscritosTotal(carregado.totalInscritos);
                  }
                  // Abre no passo onde a pessoa parou (sessionStorage por evento).
                  const salvo = sessionStorage.getItem(chaveDoPasso(porta, idNum));
                  if (salvo) setPasso(salvo as PassoChave);
                  setEstado('ok');
                }
                return;
              }
              localStorage.removeItem(chave);
            }
          }

          // Cidade/UF pré-preenchidos do perfil.
          const { data: perfilData } = await supabase
            .from('vw_meu_perfil')
            .select('cidade, uf')
            .maybeSingle();
          const pCidade = ((perfilData as { cidade?: string | null } | null)?.cidade) || '';
          const pUf = ((perfilData as { uf?: string | null } | null)?.uf) || '';

          if (ativo) {
            setDono(true);
            setEstado('ok');
            setFormaExplicitada(formaParam === 'ingresso' || formaParam === 'inscricao');
            setPerfilCidade(pCidade);
            setPerfilUf(pUf);
            setDraft((d) => ({
              ...d,
              categoria: cat,
              formaParticipar: forma,
              cidade: d.cidade || pCidade,
              uf: d.uf || pUf,
            }));
            setParticipacoes(
              sugestoesParaCategoria(cat).map((nome) => ({ ...novaParticipacao(), nome })),
            );
          }
          return;
        }

        const eventoIdNum = Number(id);
        if (Number.isNaN(eventoIdNum)) {
          if (ativo) setEstado('nao-encontrado');
          return;
        }

        const { data: ev, error: errEv } = await supabase
          .from('eventos')
          .select('*')
          .eq('id', eventoIdNum)
          .maybeSingle();
        if (errEv) throw errEv;
        if (!ev) {
          if (ativo) setEstado('nao-encontrado');
          return;
        }

        let permitido = (ev as { dono_id: string }).dono_id === user.id;
        if (!permitido) {
          const { data: eq } = await supabase
            .from('equipe_evento')
            .select('id')
            .eq('evento_id', eventoIdNum)
            .eq('usuario_id', user.id)
            .eq('status', 'ativo')
            .maybeSingle();
          permitido = !!eq;
        }
        if (!permitido) {
          if (ativo) setEstado('negado');
          return;
        }

        const ehDono = (ev as { dono_id: string }).dono_id === user.id;

        // Uma leitura só (a mesma usada ao retomar rascunho).
        const { lista, totalInscritos } = await carregarParticipacoes(eventoIdNum);

        let equipeLista: MembroEquipe[] = [];
        if (ehDono) {
          const { data: resEq } = await supabase
            .from('vw_equipe_evento')
            .select('id, email, status, o_que_significa')
            .eq('evento_id', eventoIdNum)
            .order('criado_em');
          equipeLista = (
            (resEq as { id: number; email: string | null; status: string; o_que_significa: string | null }[]) || []
          )
            .filter((e) => e.status !== 'removido')
            .map((e) => ({
              id: e.id,
              email: e.email || '',
              status: e.status,
              oQueSignifica: e.o_que_significa || '',
            }));
        }

        const evData = ev as Record<string, unknown>;

        if (ativo) {
          setDono(ehDono);
          setEventoId(eventoIdNum);
          atualizadoEmRef.current = (evData.atualizado_em as string) || null;
          setDraft(draftDeEvento(evData));
          setParticipacoes(lista);
          setEquipe(equipeLista);
          setInscritosTotal(totalInscritos);
          // Abre no passo onde a pessoa parou, quando ela já esteve aqui.
          const salvo = sessionStorage.getItem(chaveDoPasso(porta, eventoIdNum));
          if (salvo) setPasso(salvo as PassoChave);
          setEstado('ok');
        }
      } catch (e) {
        if (ativo) {
          setErro(mensagemErro(e, 'Não foi possível carregar. Tente novamente.'));
          setEstado('erro');
        }
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [navigate, id, modoEdicao, searchParams, pronto, user]);

  function marcarSalvo() {
    setSalvo(true);
    if (timerSalvo.current) window.clearTimeout(timerSalvo.current);
    timerSalvo.current = window.setTimeout(() => setSalvo(false), 2500);
  }

  // Salvamento automático: 2s depois de parar de digitar, no passo 1.
  useEffect(() => {
    if (passo !== 'evento' || estado !== 'ok') return;
    const d = draftRef.current;
    if (!d.nome.trim() || !d.inicioData || !d.inicioHora) return;

    dirtyRef.current = true;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      salvarBasico();
    }, 2000);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [draft, passo, estado]);

  // Quando a internet volta, tenta salvar de novo o que ficou pendente.
  useEffect(() => {
    function aoOnline() {
      if (dirtyRef.current) salvarBasico();
    }
    window.addEventListener('online', aoOnline);
    return () => window.removeEventListener('online', aoOnline);
  }, []);

  function tratarPerdaDeSessao() {
    // Erro de sessão NÃO é logout: só pede para tentar de novo.
    setErro('Não conseguimos confirmar seu acesso agora. Tente de novo.');
  }

  async function garantirSessao(): Promise<boolean> {
    // Usa o estado do contexto (sem chamada de rede): não desloga por falha passageira.
    if (user) return true;
    tratarPerdaDeSessao();
    return false;
  }

  function mudarDraft(patch: Partial<EventoDraft>) {
    setDraft((d) => ({ ...d, ...patch }));
  }

  async function processarArte(
    imagem: ImagemDraft,
    eventoIdNum: number,
  ): Promise<string | null | undefined> {
    if (imagem.arquivo) {
      const nome = imagem.arquivo.name || 'arte.jpg';
      const ext = nome.split('.').pop()?.toLowerCase() || 'jpg';
      const caminho = `${eventoIdNum}/arte-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('capas').upload(caminho, imagem.arquivo);
      if (error) throw error;
      const { data } = supabase.storage.from('capas').getPublicUrl(caminho);
      return data.publicUrl;
    }
    if (imagem.removida) return null;
    return undefined;
  }

  async function garantirSlug(
    nome: string,
    slugAtual: string,
    eventoIdAtual: number | null,
  ): Promise<string> {
    const existente = slugAtual.trim();
    if (existente) return existente;
    const base = slugificar(nome) || 'evento';
    let candidato = base;
    let sufixo = 2;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let q = supabase.from('eventos').select('id').eq('slug', candidato);
      if (eventoIdAtual) q = q.neq('id', eventoIdAtual);
      const { data } = await q.maybeSingle();
      if (!data) return candidato;
      candidato = `${base}-${sufixo}`;
      sufixo += 1;
    }
  }

  async function salvarBasico(): Promise<boolean> {
    if (salvandoBasicoRef.current) return false;
    const d = draftRef.current;
    if (!d.nome.trim() || !d.inicioData || !d.inicioHora) return true;

    salvandoBasicoRef.current = true;
    setEstadoSalvamento('salvando');
    try {
      const idAtualInicial = eventoIdRef.current;
      const slugFinal = await garantirSlug(d.nome, d.slug, idAtualInicial);
      const inicio = combinarDataHora(d.inicioData, d.inicioHora);
      const fimCalc = combinarDataHora(d.fimData, d.fimHora);
      const fim = fimCalc
        || (inicio ? new Date(new Date(inicio).getTime() + 3 * 3600 * 1000).toISOString() : null);

      const dados = {
        nome: d.nome.trim(),
        slug: slugFinal,
        descricao: d.descricao || null,
        categoria: d.categoria || 'outro',
        local_nome: d.localNome || null,
        endereco: d.endereco || null,
        cidade: d.cidade.trim(),
        uf: d.uf,
        cep: d.cep || null,
        inicio_em: inicio,
        fim_em: fim,
      };

      let idAtual = idAtualInicial;

      if (!idAtual) {
        const { data: novoId, error } = await supabase.rpc('criar_evento', {
          p_nome: dados.nome,
          p_slug: dados.slug,
          p_descricao: dados.descricao || '',
          p_categoria: dados.categoria,
          p_local_nome: dados.local_nome || '',
          p_endereco: dados.endereco || '',
          p_cidade: dados.cidade,
          p_uf: dados.uf,
          p_cep: dados.cep || '',
          p_inicio_em: dados.inicio_em,
          p_fim_em: dados.fim_em,
        });
        if (error) throw error;
        idAtual = Number(novoId);
        setEventoId(idAtual);
        eventoIdRef.current = idAtual;
        localStorage.setItem(`rascunho:${porta}`, String(idAtual));

        // Só grava a chave que a pessoa realmente mudou — o resto o banco
        // preenche pela categoria (criar_evento não recebe essas chaves).
        const padraoIngressos = padrao?.vende_ingressos ?? false;
        if ((d.formaParticipar === 'ingresso') !== padraoIngressos) {
          const { error: errIngresso } = await supabase
            .from('eventos')
            .update({ vende_ingressos: d.formaParticipar === 'ingresso' })
            .eq('id', idAtual);
          if (errIngresso) {
            setErro(
              'Evento criado. Não conseguimos salvar a forma de participar — ajuste na tela de editar.',
            );
          }
        }
      } else {
        let res;
        if (atualizadoEmRef.current) {
          res = await supabase
            .from('eventos')
            .update(dados)
            .eq('id', idAtual)
            .eq('atualizado_em', atualizadoEmRef.current)
            .select('atualizado_em');
          if (!res.data || res.data.length === 0) {
            const { data: evAtual } = await supabase
              .from('eventos')
              .select('atualizado_em')
              .eq('id', idAtual)
              .maybeSingle();
            atualizadoEmRef.current = evAtual?.atualizado_em ?? null;
            res = await supabase
              .from('eventos')
              .update(dados)
              .eq('id', idAtual)
              .select('atualizado_em');
          }
        } else {
          res = await supabase
            .from('eventos')
            .update(dados)
            .eq('id', idAtual)
            .select('atualizado_em');
        }
        if (res.error) throw res.error;
        if (res.data && res.data.length > 0) {
          atualizadoEmRef.current = (res.data[0] as { atualizado_em: string }).atualizado_em;
        }
      }

      // Arte única → salva nas duas colunas.
      const url = await processarArte(d.imagemDesktop, idAtual);
      const slugMudou = slugFinal !== d.slug;
      if (url !== undefined) {
        const { error: errImg } = await supabase
          .from('eventos')
          .update({ imagem_desktop_url: url, imagem_mobile_url: url })
          .eq('id', idAtual);
        if (errImg) throw errImg;
        setDraft((prev) => ({
          ...prev,
          slug: slugFinal,
          imagemDesktop: { arquivo: null, url, removida: false, carregando: false },
          imagemMobile: { arquivo: null, url, removida: false, carregando: false },
        }));
      } else if (slugMudou) {
        setDraft((prev) => ({ ...prev, slug: slugFinal }));
      }

      dirtyRef.current = false;
      setEstadoSalvamento('salvo');
      if (timerSalvoBasicoRef.current) window.clearTimeout(timerSalvoBasicoRef.current);
      timerSalvoBasicoRef.current = window.setTimeout(() => setEstadoSalvamento('ocioso'), 2500);
      return true;
    } catch (e) {
      if (sessaoPerdida(e)) {
        tratarPerdaDeSessao();
      } else if (!navigator.onLine) {
        setEstadoSalvamento('offline');
      } else {
        setErro(mensagemErro(e, 'Não foi possível salvar. Tente novamente.'));
        setEstadoSalvamento('ocioso');
      }
      return false;
    } finally {
      salvandoBasicoRef.current = false;
    }
  }

  async function salvarPasso2() {
    if (!eventoId) return;
    if (!(await garantirSessao())) return;
    const novaLista = participacoes.map((p) => ({
      ...p,
      grupos: p.grupos.map((g) => ({ ...g })),
    }));

    for (let i = 0; i < novaLista.length; i += 1) {
      const p = novaLista[i];
      const dados: Record<string, unknown> = {
        evento_id: eventoId,
        nome: p.nome.trim(),
        descricao: p.descricao || null,
        publica: p.publica,
        codigo_acesso: p.publica ? null : p.codigoAcesso || null,
        inscricoes_abrem_em: combinarDataHora(p.inscricoesAbremData, p.inscricoesAbremHora),
        inscricoes_fecham_em: combinarDataHora(p.inscricoesFechamData, p.inscricoesFechamHora),
        exige_identificacao: p.exigeIdentificacao,
        max_por_compra: p.maxPorCompra,
        ordem: i,
        vagas_total: p.temLimite ? p.vagas : null,
        valor_definido: p.valorModo !== 'a_definir',
      };
      if (p.valorModo !== 'a_definir') {
        dados.valor_centavos = p.dividirGrupos ? 0 : p.valorCentavos;
      }

      let idModalidade = p.id;
      if (idModalidade) {
        const { error } = await supabase.from('modalidades').update(dados).eq('id', idModalidade);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('modalidades')
          .insert({ ...dados, valor_centavos: (dados.valor_centavos as number) ?? 0 })
          .select('id')
          .single();
        if (error) throw error;
        idModalidade = (data as { id: number }).id;
        novaLista[i] = { ...p, id: idModalidade };
      }

      // Grupos (opções).
      const idsAtuais: number[] = [];
      for (let j = 0; j < p.grupos.length; j += 1) {
        const g = p.grupos[j];
        const gdados = {
          nome: g.nome.trim(),
          vagas: g.vagas,
          valor_centavos: g.valorCentavos,
          ordem: j,
        };
        if (g.id) {
          const { error } = await supabase.from('opcoes').update(gdados).eq('id', g.id);
          if (error) throw error;
          idsAtuais.push(g.id);
        } else {
          const { data, error } = await supabase
            .from('opcoes')
            .insert({ ...gdados, modalidade_id: idModalidade })
            .select('id')
            .single();
          if (error) throw error;
          const novoId = (data as { id: number }).id;
          p.grupos[j] = { ...g, id: novoId };
          idsAtuais.push(novoId);
        }
      }

      // Remove (ou desativa) grupos que saíram da lista.
      const { data: existentes } = await supabase
        .from('opcoes')
        .select('id')
        .eq('modalidade_id', idModalidade);
      const idsExistentes = ((existentes as { id: number }[]) || []).map((e) => e.id);
      const removidas = idsExistentes.filter((id) => !idsAtuais.includes(id));
      if (removidas.length > 0) {
        const { data: insc } = await supabase
          .from('inscricoes')
          .select('opcao_id')
          .in('opcao_id', removidas);
        const comInscrito = new Set(
          ((insc as { opcao_id: number }[]) || []).map((r) => r.opcao_id),
        );
        const paraDeletar = removidas.filter((id) => !comInscrito.has(id));
        const paraDesativar = removidas.filter((id) => comInscrito.has(id));
        if (paraDeletar.length > 0) {
          const { error } = await supabase.from('opcoes').delete().in('id', paraDeletar);
          if (error) throw error;
        }
        if (paraDesativar.length > 0) {
          const { error } = await supabase
            .from('opcoes')
            .update({ ativa: false })
            .in('id', paraDesativar);
          if (error) throw error;
        }
      }
    }

    setParticipacoes(novaLista);
  }

  async function salvarPassoPagamento() {
    // O passo "Como se paga" agora grava exclusivamente pelas funções do banco
    // (`definir_forma_de_pagamento` e `definir_destino_do_dinheiro`). Nunca
    // escrevemos em `eventos.modo_recebimento` pela tela — é coluna de dinheiro.
    return;
  }

  async function salvarPublicacao() {
    if (!eventoId) return;
    if (!(await garantirSessao())) return;
    const { error } = await supabase
      .from('eventos')
      .update({
        quem_paga_taxa: draft.quemPagaTaxa,
        contato_nome: draft.contatoNome || null,
        contato_telefone: draft.contatoTelefone || null,
      })
      .eq('id', eventoId);
    if (error) throw error;
  }

  async function publicar() {
    if (!eventoId) return;
    setSalvando(true);
    try {
      await salvarPublicacao();

      // Tipos de inscrição sem data de abertura passam a abrir agora (na publicação).
      const { error: errAbertura } = await supabase
        .from('modalidades')
        .update({ inscricoes_abrem_em: new Date().toISOString() })
        .eq('evento_id', eventoId)
        .is('inscricoes_abrem_em', null);
      if (errAbertura) throw errAbertura;

      const { error } = await supabase
        .from('eventos')
        .update({ status: 'publicado', publicado_em: new Date().toISOString() })
        .eq('id', eventoId);
      if (error) throw error;
      navigate('/meus-eventos', { state: { publicou: true, slug: draft.slug, nome: draft.nome } });
    } catch (e) {
      if (sessaoPerdida(e)) tratarPerdaDeSessao();
      else setErro(mensagemErro(e, 'Não foi possível publicar. Tente novamente.'));
    } finally {
      setSalvando(false);
    }
  }

  async function salvarRascunho() {
    setSalvando(true);
    try {
      await salvarPublicacao();
      navigate('/meus-eventos');
    } catch (e) {
      if (sessaoPerdida(e)) tratarPerdaDeSessao();
      else setErro(mensagemErro(e));
      setSalvando(false);
    }
  }

  async function continuar() {
    const indice = passos.indexOf(passo);
    let ok = true;

    if (passo === 'evento') ok = await refPasso1.current!.validar();
    else if (passo === 'inscricoes') ok = refPasso2.current!.validar();
    else if (passo === 'pagamento') ok = refPassoPagamento.current!.validar();
    else if (passo === 'rifa') {
      if (!(await garantirSessao())) return;
      ok = await refPassoRifa.current!.validar();
    } else if (passo === 'datas') ok = refPasso3.current!.validar();
    else if (passo === 'ficha') {
      if (!(await garantirSessao())) return;
      ok = await refPasso4.current!.validar();
    } else if (passo === 'publicar') ok = refPasso5.current!.validar();
    if (!ok) return;

    if (passo === 'publicar') {
      await publicar();
      return;
    }

    setSalvando(true);
    try {
      if (passo === 'evento') {
        if (eventoIdRef.current === null || dirtyRef.current) {
          const sucesso = await salvarBasico();
          if (!sucesso) return;
        }
      } else if (passo === 'inscricoes') await salvarPasso2();
      else if (passo === 'pagamento') await salvarPassoPagamento();

      marcarSalvo();
      const proximo = passos[indice + 1];
      if (proximo) setPasso(proximo);
      window.scrollTo(0, 0);
    } catch (e) {
      if (sessaoPerdida(e)) tratarPerdaDeSessao();
      else setErro(mensagemErro(e));
    } finally {
      setSalvando(false);
    }
  }

  function pularDatas() {
    const indice = passos.indexOf('datas');
    const proximo = passos[indice + 1];
    if (proximo) setPasso(proximo);
    window.scrollTo(0, 0);
  }

  function voltar() {
    const indice = passos.indexOf(passo);
    const anterior = passos[indice - 1];
    if (anterior) {
      setPasso(anterior);
      window.scrollTo(0, 0);
    }
  }

  function aoEditar(passoAlvo: PassoChave) {
    setPasso(passoAlvo);
    window.scrollTo(0, 0);
  }

  async function aoConvidar(email: string): Promise<{ ok: boolean; mensagem: string }> {
    if (!eventoId) return { ok: false, mensagem: 'Salve o evento antes de convidar.' };
    const { data, error } = await supabase.rpc('convidar_para_evento', {
      p_evento_id: Number(eventoId),
      p_email: email.trim(),
    });
    if (error) {
      if (sessaoPerdida(error)) {
        tratarPerdaDeSessao();
        return { ok: false, mensagem: '' };
      }
      return { ok: false, mensagem: mensagemErro(error, 'Não foi possível convidar.') };
    }
    await atualizarEquipe();
    return { ok: true, mensagem: (data as string) || '' };
  }

  async function atualizarEquipe() {
    if (!eventoId) return;
    const { data } = await supabase
      .from('vw_equipe_evento')
      .select('id, email, status, o_que_significa')
      .eq('evento_id', eventoId)
      .order('criado_em');
    setEquipe(
      ((data as { id: number; email: string | null; status: string; o_que_significa: string | null }[]) || [])
        .filter((e) => e.status !== 'removido')
        .map((e) => ({
          id: e.id,
          email: e.email || '',
          status: e.status,
          oQueSignifica: e.o_que_significa || '',
        })),
    );
  }

  async function aoRemoverMembro(email: string): Promise<void> {
    if (!eventoId) return;
    const { error } = await supabase.rpc('remover_da_equipe', {
      p_evento_id: Number(eventoId),
      p_email: email,
    });
    if (error) {
      if (sessaoPerdida(error)) tratarPerdaDeSessao();
      else setErro(mensagemErro(error, 'Não foi possível remover.'));
      return;
    }
    await atualizarEquipe();
  }

  // Estados de carregamento / erro / acesso.
  if (estado === 'carregando') {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-4 py-8">
          <div className="h-6 w-64 animate-pulse rounded bg-secondary-100" />
          <div className="mt-6 h-64 w-full animate-pulse rounded-lg bg-secondary-100" />
        </div>
      </div>
    );
  }

  if (estado === 'negado') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-8 text-center">
            <h1 className="text-xl font-normal text-foreground-950">Sem acesso</h1>
            <p className="mt-2 text-sm text-secondary-500">
              Você não tem acesso a este evento.
            </p>
            <button
              type="button"
              onClick={() => navigate('/meus-eventos')}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Voltar aos meus eventos
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (estado === 'nao-encontrado' || estado === 'erro') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-8 text-center">
            <h1 className="text-xl font-normal text-foreground-950">
              {estado === 'nao-encontrado' ? 'Evento não encontrado' : 'Não foi possível carregar'}
            </h1>
            <p className="mt-2 text-sm text-secondary-500">
              {estado === 'nao-encontrado' ? 'Este evento não existe.' : erro}
            </p>
            <button
              type="button"
              onClick={() => navigate('/meus-eventos')}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Voltar aos meus eventos
            </button>
          </div>
        </div>
      </div>
    );
  }

  const indicePasso = passos.indexOf(passo);
  const totalPassos = passos.length;

  return (
    <div className="min-h-screen bg-background-100">
      <header className="fixed inset-x-0 top-0 z-20 border-b border-secondary-200 bg-white">
        <div className="mx-auto w-full max-w-[720px] px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-sm font-medium text-foreground-950">
              {draft.nome.trim() || 'Novo evento'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/meus-eventos')}
              className="cursor-pointer whitespace-nowrap text-sm text-secondary-500 transition-colors hover:text-foreground-800"
            >
              Sair
            </button>
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <p className="min-w-0 text-xs text-secondary-500">
              Passo {indicePasso + 1} de {totalPassos} · {ROTULOS_PASSOS[passo]}
            </p>
            <Dica id={passo === 'rifa' ? 'criar.sorteio' : `criar.${passo}`} />
          </div>
          <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-secondary-200">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-300"
              style={{ width: `${((indicePasso + 1) / totalPassos) * 100}%` }}
            />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[720px] px-4 pb-28 pt-24">
        {inscritosTotal > 0 && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-amber-700">
              <i className="ri-information-line text-lg" />
            </span>
            <p className="text-sm text-amber-800">
              Este evento já tem {inscritosTotal}{' '}
              {inscritosTotal === 1 ? 'inscrição' : 'inscrições'}. Mudar valores não altera quem já
              se inscreveu — cada inscrição mantém o valor do dia em que foi feita.
            </p>
          </div>
        )}

        {erro && (
          <div className="mt-4 rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
            {erro}
            <button
              type="button"
              onClick={() => setErro(null)}
              className="ml-2 cursor-pointer font-medium underline"
            >
              Fechar
            </button>
          </div>
        )}

        <div className="mt-6">
          {passo === 'evento' && (
            <BlocoBasico
              ref={refPasso1}
              draft={draft}
              eventoId={eventoId}
              onChange={mudarDraft}
              perfilCidade={perfilCidade}
              perfilUf={perfilUf}
              estadoSalvamento={estadoSalvamento}
            />
          )}
          {passo === 'inscricoes' && (
            <PassoParticipacoes
              ref={refPasso2}
              participacoes={participacoes}
              slug={draft.slug}
              ehIngresso={draft.formaParticipar === 'ingresso'}
              onChange={setParticipacoes}
            />
          )}
          {passo === 'pagamento' && (
            <PassoPagamento
              ref={refPassoPagamento}
              eventoId={eventoId}
              draft={draft}
              onChange={mudarDraft}
            />
          )}
          {passo === 'rifa' && (
            <PassoRifa
              ref={refPassoRifa}
              eventoId={eventoId}
              nomeEvento={draft.nome}
              participacoes={participacoes}
            />
          )}
          {passo === 'datas' && (
            <PassoDatas
              ref={refPasso3}
              eventoId={eventoId}
              cidade={draft.cidade}
              aoPular={pularDatas}
            />
          )}
          {passo === 'ficha' && (
            <PassoFicha ref={refPasso4} participacoes={participacoes} onChange={setParticipacoes} />
          )}
          {passo === 'publicar' && (
            <PassoPublicar
              ref={refPasso5}
              draft={draft}
              participacoes={participacoes}
              equipe={equipe}
              dono={dono}
              eventoId={eventoId}
              onChange={mudarDraft}
              onPendenciaChange={(faltam) => setFaltamPublicar(faltam)}
              aoEditar={aoEditar}
              aoConvidar={aoConvidar}
              aoRemoverMembro={aoRemoverMembro}
            />
          )}
        </div>
      </main>

      <RodapePassos
        passo={indicePasso + 1}
        total={totalPassos}
        salvando={salvando}
        salvo={salvo}
        faltam={faltamPublicar}
        aoVoltar={voltar}
        aoContinuar={continuar}
        aoSalvarRascunho={salvarRascunho}
      />
    </div>
  );
}